/**
 * Автоматизация: формы правила и расчёт момента исполнения (план 73).
 *
 * ЗАЧЕМ В @app/domain. Правило создаёт и валидирует API, а исполняет воркер —
 * это два отдельных пакета БЕЗ cross-import. Общими у них могут быть только
 * `packages/*`, поэтому формы (какие бывают триггеры, отсрочки, условия,
 * действия) и чистые вычисления над ними живут здесь. Всё, что ходит в БД,
 * остаётся в своём приложении.
 */

/** Часовой пояс школы. Отсрочка «в 11:00» — местное время, не UTC. */
export const SCHOOL_TZ_OFFSET_HOURS = 3;

export const AUTOMATION_TRIGGERS = [
  'deal_stage_entered',
  'occ_enrolled',
  'occ_enroll_cancelled',
  'occ_attended',
  'occ_no_show',
  'occ_before_start',
  'occ_after_end',
  /** Продан абонемент (план 141 §2.6) — событие КОНТАКТА, а не сделки или занятия. */
  'subscription_sold',
] as const;
export type AutomationTrigger = (typeof AUTOMATION_TRIGGERS)[number];

export const AUTOMATION_ACTIONS = [
  'send_message',
  'create_task',
  'update_field',
  'change_stage',
  'notify_employee',
] as const;
export type AutomationAction = (typeof AUTOMATION_ACTIONS)[number];

/**
 * Встроенные правила сделки (план 162): поведение плана 161, которое CRM
 * исполняет сама, в транзакции события, — а владелец видит его в
 * «Автоматизации» и выключает. Строка `automation_rules` с `system_key`,
 * `trigger_kind = action_kind = 'system'`; править и удалять её нельзя.
 *
 * `'system'` в `AUTOMATION_TRIGGERS` и `AUTOMATION_ACTIONS` НЕ входит
 * сознательно: DTO создания правил собран из них и должен по-прежнему его
 * отвергать — встроенное правило заводит только миграция.
 *
 * Ключи совпадают с CHECK `automation_rules_system_key_chk`. Тексты — одни
 * для API, админки и журнала.
 */
export const SYSTEM_RULE_KEYS = ['deal_branch_from_client', 'deal_children'] as const;
export type SystemRuleKey = (typeof SYSTEM_RULE_KEYS)[number];

export interface SystemRuleInfo {
  key: SystemRuleKey;
  name: string;
  description: string;
  /** Что сломается, если выключить: показывается рядом с выключателем. */
  warning: string;
}

const SYSTEM_RULE_WARNING =
  'Если выключить, робот «Купил абонемент» не сможет перевести часть сделок: стадия требует «Локацию», а сделку ребёнка он ищет среди участников.';

export const SYSTEM_RULES: Record<SystemRuleKey, SystemRuleInfo> = {
  deal_branch_from_client: {
    key: 'deal_branch_from_client',
    name: 'Локация сделки — из филиала клиента',
    description:
      'Когда у клиента появляется или меняется основной филиал (карточка, мини-апп, склейка) или у сделки меняется клиент, CRM ставит этот филиал в пустую «Локацию» открытых сделок клиента. Заполненную не меняет. Локацию, которую клиент сам называет боту при знакомстве, бот записывает в сделку отдельно — этот выключатель её не касается.',
    warning: SYSTEM_RULE_WARNING,
  },
  deal_children: {
    key: 'deal_children',
    name: 'Дети клиента — участники его сделки',
    description:
      'Когда заводится сделка, меняется её клиент, добавляется связь родитель–ребёнок, склеиваются карточки или в карточке родителя заводят нового ребёнка, CRM добавляет детей клиента участниками его открытых сделок.',
    warning: SYSTEM_RULE_WARNING,
  },
};

/** Ключ из базы — известный ли каталогу. Незнакомый (будущий) не роняет чтение. */
export function isSystemRuleKey(value: unknown): value is SystemRuleKey {
  return typeof value === 'string' && (SYSTEM_RULE_KEYS as readonly string[]).includes(value);
}

/** Триггеры, у которых событие — само занятие, а не сделка. */
export const OCCURRENCE_TRIGGERS: readonly AutomationTrigger[] = [
  'occ_enrolled',
  'occ_enroll_cancelled',
  'occ_attended',
  'occ_no_show',
  'occ_before_start',
  'occ_after_end',
];

/** Триггеры контакта: событие случается с человеком, сделка резолвится по нему. */
export const CONTACT_TRIGGERS: readonly AutomationTrigger[] = ['subscription_sold'];

/** Календарные триггеры: их запускает тик по времени, а не действие человека. */
export const CALENDAR_TRIGGERS: readonly AutomationTrigger[] = [
  'occ_before_start',
  'occ_after_end',
];

export type TimeUnit = 'minutes' | 'hours' | 'days';

export type AutomationTiming =
  | { kind: 'immediate' }
  | { kind: 'delay'; amount: number; unit: TimeUnit }
  /** «через N дней в ЧЧ:ММ» школьного времени — сценарий владельца С1. */
  | { kind: 'delay_days_at'; days: number; at: string }
  | { kind: 'before_date'; amount: number; unit: TimeUnit }
  | { kind: 'after_date'; amount: number; unit: TimeUnit };

export type ConditionOperator =
  | 'eq'
  | 'neq'
  | 'empty'
  | 'not_empty'
  | 'gt'
  | 'lt'
  | 'gte'
  | 'lte'
  | 'in';

export interface ConditionLeaf {
  field: string;
  op: ConditionOperator;
  value?: unknown;
}

export type ConditionNode = ConditionLeaf | { all: ConditionNode[] } | { any: ConditionNode[] };

/**
 * Поля, доступные условиям и правке. Whitelist, а не «любая колонка»: правило
 * — это ввод пользователя, и произвольное имя поля превратило бы автоматизацию
 * в дыру (чужие колонки, обход RLS-логики, платёжные поля).
 *
 * `readable` — можно спрашивать в условии; `writable` — можно менять действием
 * `update_field`. Суммы сделки здесь нет СОЗНАТЕЛЬНО: она вычисляемая
 * (активные абонементы клиента либо цена пробного), колонки не существует.
 */
export const AUTOMATION_FIELDS: Record<
  string,
  { entity: 'deal' | 'contact' | 'occurrence' | 'computed'; readable: boolean; writable: boolean }
> = {
  'deal.stage_id': { entity: 'deal', readable: true, writable: false }, // меняется change_stage
  'deal.stage_kind': { entity: 'deal', readable: true, writable: false },
  'deal.branch_id': { entity: 'deal', readable: true, writable: true },
  'deal.city_id': { entity: 'deal', readable: true, writable: true },
  'deal.source_id': { entity: 'deal', readable: true, writable: true },
  'deal.responsible_employee_id': { entity: 'deal', readable: true, writable: true },
  'deal.title': { entity: 'deal', readable: true, writable: true },

  'contact.status': { entity: 'contact', readable: true, writable: false }, // статус считает воронка
  'contact.client_type': { entity: 'contact', readable: true, writable: true },
  'contact.source_id': { entity: 'contact', readable: true, writable: true },
  'contact.responsible_employee_id': { entity: 'contact', readable: true, writable: true },
  'contact.branch_id': { entity: 'contact', readable: true, writable: false },
  'contact.has_phone': { entity: 'contact', readable: true, writable: false },

  'occurrence.kind': { entity: 'occurrence', readable: true, writable: false },
  'occurrence.branch_id': { entity: 'occurrence', readable: true, writable: false },
  'occurrence.group_id': { entity: 'occurrence', readable: true, writable: false },

  /** Есть действующий абонемент — сценарии С1/С2 различаются ровно этим. */
  'client.has_active_subscription': { entity: 'computed', readable: true, writable: false },
  /**
   * Действует ПРОБНЫЙ абонемент — то есть человек пришёл по пробному товару
   * (правка владельца 27.08: «при записи на пробное ставить задачу»).
   *
   * Отдельным полем, а не значением `occurrence.kind`: пробное у нас — это ТИП
   * ТОВАРА (`subscription_templates.type='trial'`), а занятие при этом самое
   * обычное групповое. Спрашивать «тип занятия = пробное» было бы неверно: у
   * занятия такого типа не существует.
   */
  'client.has_trial_subscription': { entity: 'computed', readable: true, writable: false },

  /**
   * Проданный абонемент (план 141 §2.6) — только у события `subscription_sold`:
   * робот С2' переводит сделку в «Абонемент приобретён», когда продан
   * абонемент, а не разовое или пробное (`sale.sessions_count > 1`).
   */
  'sale.template_type': { entity: 'computed', readable: true, writable: false },
  'sale.sessions_count': { entity: 'computed', readable: true, writable: false },
  /**
   * У клиента нет другого подходящего абонемента, проданного раньше этого
   * (план 141 §6.3.1): благодарность с памяткой — только при первой покупке.
   */
  'sale.is_first_subscription': { entity: 'computed', readable: true, writable: false },
  /**
   * Проданный товар — абонемент по определению §2.3.1 (`qualifying-subscription.ts`):
   * безлимит или пакет > 1 занятия. Правило «купил → Абонемент приобретён»
   * держится на нём, а не на паре template_type/sessions_count — у безлимита
   * sessions_count пуст, и «> 1» никогда не проходило бы.
   */
  'sale.is_qualifying_subscription': { entity: 'computed', readable: true, writable: false },

  /**
   * Запись занятия (план 141 §6.3.1): тип шаблона абонемента записи (пробное
   * от разового отличается только им — «Разовое» после списания тоже
   * exhausted) и СВЕЖИЙ статус записи при исполнении (для «не пришёл»:
   * scheduled/no_show, а не attended).
   */
  'attendance.template_type': { entity: 'computed', readable: true, writable: false },
  'attendance.status': { entity: 'computed', readable: true, writable: false },
  /** Сколько посещений «пришёл» у клиента всего, с импортированной историей. */
  'client.attended_visits_count': { entity: 'computed', readable: true, writable: false },
};

/**
 * Русские подписи полей — ОТДЕЛЬНОЙ картой, не полем внутри `AUTOMATION_FIELDS`.
 *
 * Белый список сверяется тестом на точную форму записи (`toEqual`), и лишний
 * ключ в ней уронил бы его; к тому же воркер держит свою копию проверок и
 * ничего не знает о подписях — им место там, где их читают, то есть в
 * интерфейсе.
 *
 * Справочник, из которого выбирается значение поля в форме правила: `null` —
 * значит вводится текстом (или это да/нет).
 */
export type AutomationFieldSource =
  | 'branch'
  | 'city'
  | 'source'
  | 'employee'
  | 'stage_kind'
  | 'template_type'
  | 'attendance_status'
  | null;

export const AUTOMATION_FIELD_UI: Record<string, { label: string; source: AutomationFieldSource }> =
  {
    'deal.stage_kind': { label: 'Вид стадии сделки', source: 'stage_kind' },
    'deal.branch_id': { label: 'Локация сделки', source: 'branch' },
    'deal.city_id': { label: 'Город сделки', source: 'city' },
    'deal.source_id': { label: 'Источник сделки', source: 'source' },
    'deal.responsible_employee_id': { label: 'Ответственный за сделку', source: 'employee' },

    'contact.status': { label: 'Сегмент контакта', source: null },
    'contact.client_type': { label: 'Тип контакта', source: null },
    'contact.source_id': { label: 'Источник контакта', source: 'source' },
    'contact.responsible_employee_id': { label: 'Ответственный за контакт', source: 'employee' },
    'contact.branch_id': { label: 'Локация контакта', source: 'branch' },
    'contact.has_phone': { label: 'Телефон указан', source: null },

    'occurrence.kind': { label: 'Тип занятия', source: null },
    'occurrence.branch_id': { label: 'Локация занятия', source: 'branch' },

    'client.has_active_subscription': { label: 'Есть действующий абонемент', source: null },
    'client.has_trial_subscription': { label: 'Есть пробный абонемент', source: null },

    'sale.template_type': { label: 'Тип проданного абонемента', source: 'template_type' },
    'sale.sessions_count': { label: 'Занятий в проданном абонементе', source: null },
    'sale.is_first_subscription': { label: 'Первый абонемент клиента', source: null },
    'sale.is_qualifying_subscription': {
      label: 'Продан абонемент (пакет > 1 или безлимит)',
      source: null,
    },

    'attendance.template_type': { label: 'Тип абонемента записи', source: 'template_type' },
    'attendance.status': { label: 'Статус записи', source: 'attendance_status' },
    'client.attended_visits_count': { label: 'Посещений у клиента', source: null },
  };

/**
 * Поля, доступные в условиях правила. Из белого списка исключены те, что
 * читаются машиной, но человеку в условии не нужны: `deal.stage_id` (условие на
 * стадию задаётся самим событием), `deal.title` (свободный текст) и
 * `occurrence.group_id` (в интерфейсе выбирают направление, а не id занятия).
 */
export const AUTOMATION_CONDITION_FIELDS: string[] = Object.keys(AUTOMATION_FIELD_UI).filter(
  (key) => AUTOMATION_FIELDS[key]?.readable === true,
);

/**
 * Виды стадий сделки: их ПЯТЬ. Единственный источник правды — отсюда типы
 * расходятся по API и админке (план 77).
 *
 * `client` («Текущий клиент») — терминальный вид, как won и lost: сделка
 * завершена. Заведён отдельно от `won` намеренно: туда падают доимпортируемые
 * клиенты, и посчитай мы их выигранными — статистика привлечения и продаж
 * новым клиентам стала бы враньём.
 */
export const DEAL_STAGE_KINDS = ['open', 'client', 'won', 'lost', 'invalid'] as const;

export type DealStageKind = (typeof DEAL_STAGE_KINDS)[number];

/**
 * Группы стадий для тоглов над воронкой (план 77). Группа — ФУНКЦИЯ от вида
 * стадии, а не отдельное поле: в базе её нет и заводить не надо, иначе завтра
 * вид и группа разъедутся, и никто не скажет, кто из них прав.
 *
 * Порядок массива задаёт и порядок тоглов, и порядок БЛОКОВ стадий в канбане:
 * сперва рабочие, потом текущие клиенты, потом исходы.
 */
export const STAGE_GROUPS = [
  { key: 'working', label: 'В работе', kinds: ['open'] },
  { key: 'clients', label: 'Текущие клиенты', kinds: ['client'] },
  { key: 'won', label: 'Сделка заключена', kinds: ['won'] },
  { key: 'lost', label: 'Сделка провалена', kinds: ['lost', 'invalid'] },
] as const satisfies ReadonlyArray<{
  key: string;
  label: string;
  kinds: readonly DealStageKind[];
}>;

export type StageGroupKey = (typeof STAGE_GROUPS)[number]['key'];

/** Группа стадии. Незнакомый вид не роняет воронку — уводим в исходы. */
export function stageGroupOf(kind: string): StageGroupKey {
  const group = STAGE_GROUPS.find((g) => (g.kinds as readonly string[]).includes(kind));
  return group?.key ?? 'lost';
}

/**
 * Ранг группы для сортировки стадий блоками. Внутри блока порядок задаёт
 * школа (`sort_order`), а сами блоки переставлять нельзя: «провалена» между
 * рабочими стадиями читается как ошибка настройки.
 */
export function stageGroupRank(kind: string): number {
  const idx = STAGE_GROUPS.findIndex((g) => (g.kinds as readonly string[]).includes(kind));
  return idx === -1 ? STAGE_GROUPS.length : idx;
}

const UNIT_MS: Record<TimeUnit, number> = {
  minutes: 60_000,
  hours: 3_600_000,
  days: 86_400_000,
};

/**
 * Пояс по умолчанию, когда у филиала пояс не задан или задан неверно.
 */
export const DEFAULT_TIME_ZONE = 'Europe/Moscow';

/** Известен ли пояс платформе (IANA): `Intl` бросает RangeError на опечатке. */
export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Пояс филиала — свободный текст в карточке; опечатка («Moscow») не должна
 * ронять продажу, отметку и тик скана. Неизвестный пояс → Москва.
 */
export function safeTimeZone(tz: string | null | undefined): string {
  return tz && isValidTimeZone(tz) ? tz : DEFAULT_TIME_ZONE;
}

/**
 * Смещение пояса IANA относительно UTC (минуты) на заданный момент — через
 * `Intl`, как в school-time.ts: переходы на летнее время и пояса без
 * фиксированного смещения считает платформа, а не таблица в коде.
 */
export function zoneOffsetMinutes(at: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);
  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? '0');
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second'),
  );
  return Math.round((asUtc - at.getTime()) / 60_000);
}

/**
 * Когда исполнять правило.
 *
 * @param at    момент события (для календарных — время занятия).
 * @param timeZone  IANA-пояс ФИЛИАЛА события (план 141 §6.3.4, решение
 *                  владельца: «12:00» — по поясу филиала, Москву не зашивать).
 *                  Без параметра — прежнее поведение (фиксированная Москва).
 * @returns дата запуска; для 'immediate' — сам момент события.
 *
 * `delay_days_at` считает МЕСТНОЕ время: «завтра в 11:00» для события в 23:50
 * должно дать 11:00 следующего местного дня, а не UTC-следующего. Считаем
 * сдвигом на смещение пояса, а не `toLocaleString`: в воркере и в API одна
 * арифметика, без зависимости от TZ процесса.
 */
export function computeScheduledAt(timing: AutomationTiming, at: Date, timeZone?: string): Date {
  switch (timing.kind) {
    case 'immediate':
      return at;
    case 'delay':
      return new Date(at.getTime() + timing.amount * UNIT_MS[timing.unit]);
    case 'before_date':
      return new Date(at.getTime() - timing.amount * UNIT_MS[timing.unit]);
    case 'after_date':
      return new Date(at.getTime() + timing.amount * UNIT_MS[timing.unit]);
    case 'delay_days_at': {
      const [hh, mm] = timing.at.split(':').map((n) => Number(n));
      const zone = timeZone ? safeTimeZone(timeZone) : undefined;
      const shiftAt = zone
        ? zoneOffsetMinutes(at, zone) * UNIT_MS.minutes
        : SCHOOL_TZ_OFFSET_HOURS * UNIT_MS.hours;
      // Переводим в местные сутки, двигаем дату, ставим время, возвращаем в UTC.
      const local = new Date(at.getTime() + shiftAt);
      const target = Date.UTC(
        local.getUTCFullYear(),
        local.getUTCMonth(),
        local.getUTCDate() + timing.days,
        hh ?? 0,
        mm ?? 0,
      );
      if (!zone) return new Date(target - shiftAt);
      // Смещение берём у ЦЕЛЕВОГО момента: между событием и «завтра 12:00»
      // пояс мог перейти на летнее/зимнее время.
      const guess = new Date(target - shiftAt);
      return new Date(target - zoneOffsetMinutes(guess, zone) * UNIT_MS.minutes);
    }
  }
}

/** Человеческая подпись отсрочки для карточки правила («сразу», «через 2 часа»). */
export function describeTiming(timing: AutomationTiming): string {
  const unitWord = (amount: number, unit: TimeUnit): string => {
    const forms: Record<TimeUnit, [string, string, string]> = {
      minutes: ['минуту', 'минуты', 'минут'],
      hours: ['час', 'часа', 'часов'],
      days: ['день', 'дня', 'дней'],
    };
    const n = Math.abs(amount) % 100;
    const n1 = n % 10;
    const [one, few, many] = forms[unit];
    if (n > 10 && n < 20) return many;
    if (n1 > 1 && n1 < 5) return few;
    if (n1 === 1) return one;
    return many;
  };

  switch (timing.kind) {
    case 'immediate':
      return 'сразу';
    case 'delay':
      return `через ${timing.amount} ${unitWord(timing.amount, timing.unit)}`;
    case 'delay_days_at':
      return timing.days === 1
        ? `на следующий день в ${timing.at}`
        : `через ${timing.days} ${unitWord(timing.days, 'days')} в ${timing.at}`;
    case 'before_date':
      return `за ${timing.amount} ${unitWord(timing.amount, timing.unit)} до занятия`;
    case 'after_date':
      return `через ${timing.amount} ${unitWord(timing.amount, timing.unit)} после занятия`;
  }
}

/**
 * Проверить дерево условий на готовом снапшоте значений.
 *
 * Снапшот собирает вызывающий (у API и воркера разные источники), здесь —
 * только логика. `null`/пустое дерево = условий нет, правило проходит.
 */
export function evaluateConditions(
  node: ConditionNode | null | undefined,
  values: Record<string, unknown>,
): boolean {
  if (!node) return true;
  if ('all' in node) return node.all.every((child) => evaluateConditions(child, values));
  if ('any' in node) return node.any.some((child) => evaluateConditions(child, values));

  const actual = values[node.field];
  switch (node.op) {
    case 'eq':
      return String(actual ?? '') === String(node.value ?? '');
    case 'neq':
      return String(actual ?? '') !== String(node.value ?? '');
    case 'empty':
      return actual === null || actual === undefined || actual === '' || actual === false;
    case 'not_empty':
      return !(actual === null || actual === undefined || actual === '' || actual === false);
    case 'in':
      return Array.isArray(node.value) && node.value.map(String).includes(String(actual ?? ''));
    case 'gt':
    case 'lt':
    case 'gte':
    case 'lte': {
      const a = Number(actual);
      const b = Number(node.value);
      if (Number.isNaN(a) || Number.isNaN(b)) return false;
      if (node.op === 'gt') return a > b;
      if (node.op === 'lt') return a < b;
      if (node.op === 'gte') return a >= b;
      return a <= b;
    }
  }
}

/** Глубина цепочки правил: дальше обрываем, иначе A→B→A крутился бы вечно. */
export const AUTOMATION_MAX_HOPS = 3;

/** Сколько активных правил «сообщение клиенту» разрешено школе. */
export const AUTOMATION_MAX_MESSAGE_RULES = 20;
